import React from 'react';
import {
  Action,
  Fieldset, FormHandlers, FormSkeleton, ICON_ALERT_CIRCLE_OUTLINE, ICON_CHECK_CIRCLE_OUTLINE, Message, Portal, Spacing, Stack, useModal,
} from '@doveit/bricks';
import hash from 'object-hash';
import ViewContactPreview from '../../../../contact/containers/view-contact-preview/ViewContactPreview';
import { isEmpty } from '../../../../utils/object/object';
import { AssignmentWizardData, WizardContact } from '../../../../providers/api/dtos';
import SimpleModal from '../../../../components/simple-modal/SimpleModal';
import { AssignmentWizardStepProps } from '../assignment-wizard/types';
import NonLegalSellerAmlForm, { NonLegalSellerAmlFormModel } from './non-legal-seller-aml-form/NonLegalSellerAmlForm';
import LegalEntityAmlForm, { LegalEntityAmlFormModel } from '../../../../contact/components/legal-entity-aml-form/LegalEntityAmlForm';
import {
  assignmentWizardDataToLegalEntityAmlFormModel, legalEntityAmlFormModelToAssignmentWizardData, nonLegalSellerAmlFormModelToWizardContact, wizardContactToNonLegalSellerAmlFormModel,
} from './mappers';

const AssignmentWizardAntiMoneyLaundering: React.FC<AssignmentWizardStepProps> = ({
  initialData,
  loading,
  formRef,
  onSubmit,
}) => {
  const legalSellerStepRef = React.useRef<FormHandlers>(null) as React.MutableRefObject<FormHandlers>;
  const nonLegalSellerAmlFormRef = React.useRef<FormHandlers>(null) as React.MutableRefObject<FormHandlers>;
  const nonLegalSellerAmlModal = useModal<{ seller: WizardContact }>();
  const [isLegalStepDirty, setIsLegalStepDirty] = React.useState(false);
  const [assignmentWizardData, setAssignmentWizardData] = React.useState(initialData);

  React.useEffect(() => {
    setAssignmentWizardData(initialData);
  }, [initialData]);

  const isLegalSeller = React.useMemo(
    () => assignmentWizardData.contacts?.length === 1 && !!assignmentWizardData.contacts[0].legal,
    [assignmentWizardData.contacts],
  );

  const nonLegalSellers = React.useMemo(
    () => (!isLegalSeller ? (assignmentWizardData.contacts?.filter(({ legal }) => !legal) || []) : []),
    [isLegalSeller, assignmentWizardData.contacts],
  );

  React.useImperativeHandle(formRef, () => ({
    handleSubmit: (e: React.FormEvent<HTMLFormElement>) => {
      if (isLegalSeller) {
        legalSellerStepRef.current?.handleSubmit(e);
      } else {
        onSubmit(assignmentWizardData, isLegalStepDirty);
      }
    },
  } as FormHandlers), [isLegalStepDirty, isLegalSeller, assignmentWizardData, onSubmit]);

  const handleLegalSellerFormSubmit = React.useCallback((formValues: LegalEntityAmlFormModel) => {
    const updatedAssignmentWizardData = legalEntityAmlFormModelToAssignmentWizardData(assignmentWizardData, formValues);
    const isFormDirty = (legalSellerStepRef?.current as any)?.dirty ?? false;

    onSubmit(updatedAssignmentWizardData, isFormDirty);
  }, [assignmentWizardData, onSubmit]);

  const sellerHasMissingAml = React.useCallback(
    (seller: WizardContact) => !seller.antiMoneyLaundering || isEmpty(seller),
    [],
  );

  const message = React.useMemo(() => {
    if (isLegalSeller) return undefined;

    if (nonLegalSellers.length === 0) {
      return 'Per compilare la dichiarazione di antiriciclaggio è necessario selezionare o inserire un venditore.';
    }

    const nonLegalBuyersWithMissingAml = nonLegalSellers.filter(sellerHasMissingAml);

    switch (nonLegalBuyersWithMissingAml.length) {
      case 0:
        return undefined;
      case 1:
        return 'Compila i dati relativi all\'antiriciclaggio per il venditore.';
      default:
        return 'Compila i dati relativi all\'antiriciclaggio per ogni venditore.';
    }
  }, [sellerHasMissingAml, isLegalSeller, nonLegalSellers]);

  const handleBuyerAmlUpsert = React.useCallback((formValues: NonLegalSellerAmlFormModel, sellerToAddAmlTo?: WizardContact) => {
    if (!sellerToAddAmlTo) return;

    const updatedAssignmentWizardData: AssignmentWizardData = {
      ...assignmentWizardData,
      excludeAntiMoneyLaundering: false,
      contacts: assignmentWizardData.contacts?.map((seller) => (hash(sellerToAddAmlTo) === hash(seller)
        ? nonLegalSellerAmlFormModelToWizardContact(seller, formValues)
        : seller)),
    };

    setAssignmentWizardData(updatedAssignmentWizardData);
    setIsLegalStepDirty(true);

    nonLegalSellerAmlModal.close();
  }, [nonLegalSellerAmlModal, assignmentWizardData]);

  if (loading) return <FormSkeleton />;

  return (
    <>
      <Stack gap={300}>
        {message && (
          <Message
            type="neutral"
            message={message}
          />
        )}

        {isLegalSeller && (
          <LegalEntityAmlForm
            buyer={assignmentWizardData.contacts![0]}
            innerRef={legalSellerStepRef}
            onSubmit={handleLegalSellerFormSubmit}
            initialValues={assignmentWizardDataToLegalEntityAmlFormModel(assignmentWizardData)}
          />
        )}

        {!isLegalSeller && (
          <>
            {nonLegalSellers.length > 1 && <Fieldset legend="Elenco venditori" />}

            <Stack gap={150}>
              {nonLegalSellers.map((seller) => (
                <ViewContactPreview
                  key={seller.id}
                  title="Antiriciclaggio"
                  color={sellerHasMissingAml(seller) ? 'critical' : 'success'}
                  icon={{
                    path: sellerHasMissingAml(seller) ? ICON_ALERT_CIRCLE_OUTLINE : ICON_CHECK_CIRCLE_OUTLINE,
                  }}
                  contactId={seller.id!}
                  primaryActions={[
                    <Action
                      label={sellerHasMissingAml(seller) ? 'Compila' : 'Modifica'}
                      onClick={() => nonLegalSellerAmlModal.open({ seller })}
                      size="S"
                    />,
                  ]}
                />
              ))}
            </Stack>
          </>
        )}
      </Stack>

      <Portal>
        {nonLegalSellerAmlModal.data?.seller && (
          <SimpleModal
            {...nonLegalSellerAmlModal}
            title={`${sellerHasMissingAml(nonLegalSellerAmlModal.data.seller) ? 'Compila' : 'Modifica'} dati antiriciclaggio`}
            aria-label={`${sellerHasMissingAml(nonLegalSellerAmlModal.data.seller) ? 'Compila' : 'Modifica'} dati antiriciclaggio`}
            footer={(
              <Action
                label={sellerHasMissingAml(nonLegalSellerAmlModal.data.seller) ? 'Compila' : 'Modifica'}
                color="primary"
                emphasis="high"
                aria-label="Pulsante per salvare i dati relativi all'antiriciclaggio"
                onClick={() => nonLegalSellerAmlFormRef.current?.handleSubmit()}
              />
            )}
          >
            <ViewContactPreview contactId={nonLegalSellerAmlModal.data.seller.id!} />
            <Spacing margin={[300, 0, 0]}>
              <NonLegalSellerAmlForm
                initialValues={wizardContactToNonLegalSellerAmlFormModel(nonLegalSellerAmlModal.data.seller)}
                onSubmit={(formValues) => handleBuyerAmlUpsert(formValues, nonLegalSellerAmlModal.data?.seller)}
                formRef={nonLegalSellerAmlFormRef}
              />
            </Spacing>
          </SimpleModal>
        )}
      </Portal>
    </>
  );
};

export default AssignmentWizardAntiMoneyLaundering;
